import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import {
    PasswordPolicyFeedback,
    passwordPolicyBlocksSubmit,
    usePasswordPolicyFeedback,
} from '@/components/auth/PasswordPolicyFeedback';

type RegistrationStep = 'validate' | 'password' | 'totp' | 'backup' | 'complete';

export function RegisterContent() {
    const log = createComponentLogger('RegisterContent');
    const params = useParams();
    const navigate = useNavigate();
    const token = params.token;
    if (token === undefined) {
        throw new Error('Registration token is required');
    }
    const [step, setStep] = useState<RegistrationStep>('validate');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [backupCodesAcknowledged, setBackupCodesAcknowledged] = useState(false);
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const passwordPolicyState = usePasswordPolicyFeedback(password);

    // Step 1: Validate token
    const validateTokenMutation = useMutation({
        mutationFn: (pwd?: string) => authApi.verifyEmail(token, pwd),
        onSuccess: () => {
            if (password) {
                // Password was set, move to TOTP step
                setStep('totp');
            } else {
                // No password yet, move to password step
                setStep('password');
            }
        },
    });

    // Step 2: Setup TOTP
    const { data: totpSetup } = useQuery({
        queryKey: ['totp-setup'],
        queryFn: async () => await authApi.setupTOTP(),
        enabled: step === 'totp',
    });

    const enableTotpMutation = useMutation({
        mutationFn: authApi.enableTOTP,
        onSuccess: () => {
            // Generate backup codes
            generateBackupCodesMutation.mutate();
        },
    });

    // Step 3: Generate backup codes
    const generateBackupCodesMutation = useMutation({
        mutationFn: authApi.generateBackupCodes,
        onSuccess: (data) => {
            setBackupCodes(data.codes);
            setStep('backup');
        },
    });

    // Auto-validate token on mount
    useEffect(() => {
        if (token && step === 'validate') {
            validateTokenMutation.mutate(undefined);
        }
    }, [token]);

    const handlePasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            return;
        }
        if (passwordPolicyBlocksSubmit(passwordPolicyState, password)) {
            return;
        }
        // Validate token with password
        validateTokenMutation.mutate(password);
    };

    const handleTotpSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!totpCode || totpCode.length !== 6 || !totpSetup) {
            return;
        }
        enableTotpMutation.mutate({
            secret: totpSetup.secret,
            code: totpCode,
        });
    };

    const handleBackupCodesContinue = () => {
        if (!backupCodesAcknowledged) {
            return;
        }
        // Auto-login after registration
        // The user is already activated, so we can redirect to login
        // Or we could attempt to log them in automatically
        setStep('complete');
        runInBackground(navigate('/dashboard'), (err) => {
            log.error('Error navigating to dashboard:', err);
        });
    };

    const handleCopyBackupCodes = () => {
        runInBackground(navigator.clipboard.writeText(backupCodes.join('\n')), (err) => {
            log.error('Error copying backup codes to clipboard:', err);
        });
    };

    if (step === 'validate') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="text-center">Validating Invitation</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {validateTokenMutation.isPending && (
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-muted-foreground">
                                    Validating your invitation...
                                </p>
                            </div>
                        )}

                        {validateTokenMutation.isError && (
                            <>
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        <div className="font-semibold mb-2">Invalid Invitation</div>
                                        <p className="text-sm">
                                            {validateTokenMutation.error instanceof Error
                                                ? validateTokenMutation.error.message
                                                : 'The invitation token is invalid, expired, or has already been used.'}
                                        </p>
                                    </AlertDescription>
                                </Alert>
                                <Button
                                    onClick={() => {
                                        runInBackground(navigate('/login'), (err) => {
                                            log.error('Error navigating to login:', err);
                                        });
                                    }}
                                    variant="outline"
                                    className="w-full"
                                >
                                    Back to Login
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (step === 'password') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="text-center">Set Your Password</CardTitle>
                        <CardDescription className="text-center">
                            Create a secure password for your account
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={validateTokenMutation.isPending}
                                />
                                <PasswordPolicyFeedback
                                    password={password}
                                    state={passwordPolicyState}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="Confirm password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    disabled={validateTokenMutation.isPending}
                                />
                            </div>

                            {password && confirmPassword && password !== confirmPassword && (
                                <Alert variant="destructive">
                                    <AlertDescription>Passwords do not match</AlertDescription>
                                </Alert>
                            )}

                            {validateTokenMutation.error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {validateTokenMutation.error instanceof Error
                                            ? validateTokenMutation.error.message
                                            : 'Failed to set password'}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={
                                    validateTokenMutation.isPending ||
                                    !confirmPassword ||
                                    password !== confirmPassword ||
                                    passwordPolicyBlocksSubmit(passwordPolicyState, password)
                                }
                            >
                                {validateTokenMutation.isPending
                                    ? 'Setting Password...'
                                    : 'Continue'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (step === 'totp') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="text-center">
                            Enable Multifactor Authentication
                        </CardTitle>
                        <CardDescription className="text-center">
                            Scan the QR code with your authenticator app
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {totpSetup && (
                            <>
                                <Alert>
                                    <AlertDescription>
                                        Use an authenticator app (Google Authenticator, Authy, etc.)
                                        to scan this QR code.
                                    </AlertDescription>
                                </Alert>

                                <div className="flex justify-center p-4 bg-white border rounded-lg">
                                    <img src={totpSetup.qr_code} alt="TOTP QR Code" />
                                </div>

                                <div className="space-y-2">
                                    <Label>Or enter this secret manually:</Label>
                                    <Input
                                        value={totpSetup.secret}
                                        readOnly
                                        className="font-mono text-sm"
                                    />
                                </div>

                                <form onSubmit={handleTotpSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="totpCode">Verification Code</Label>
                                        <Input
                                            id="totpCode"
                                            type="text"
                                            value={totpCode}
                                            onChange={(e) => setTotpCode(e.target.value)}
                                            placeholder="Enter 6-digit code"
                                            maxLength={6}
                                            required
                                            disabled={enableTotpMutation.isPending}
                                        />
                                    </div>

                                    {enableTotpMutation.error && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                {enableTotpMutation.error instanceof Error
                                                    ? enableTotpMutation.error.message
                                                    : 'Failed to enable MFA'}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={
                                            enableTotpMutation.isPending || totpCode.length !== 6
                                        }
                                    >
                                        {enableTotpMutation.isPending
                                            ? 'Verifying...'
                                            : 'Enable MFA'}
                                    </Button>
                                </form>
                            </>
                        )}

                        {!totpSetup && (
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-muted-foreground">Loading MFA setup...</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (step === 'backup') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="text-center">Save Your Backup Codes</CardTitle>
                        <CardDescription className="text-center">
                            Store these codes in a safe place. You can use them to access your
                            account if you lose your authenticator device.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Alert>
                            <AlertDescription>
                                Each backup code can only be used once. Save them in a secure
                                location.
                            </AlertDescription>
                        </Alert>

                        <div className="p-4 bg-muted rounded-lg space-y-2 max-h-64 overflow-y-auto">
                            {backupCodes.map((code, idx) => (
                                <div key={idx} className="font-mono text-sm">
                                    {code}
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <Button
                                onClick={handleCopyBackupCodes}
                                variant="outline"
                                className="flex-1"
                            >
                                Copy Codes
                            </Button>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="acknowledge"
                                checked={backupCodesAcknowledged}
                                onCheckedChange={(checked) =>
                                    setBackupCodesAcknowledged(checked === true)
                                }
                            />
                            <Label
                                htmlFor="acknowledge"
                                className="text-sm font-normal cursor-pointer"
                            >
                                I have saved my backup codes in a secure location
                            </Label>
                        </div>

                        <Button
                            onClick={handleBackupCodesContinue}
                            className="w-full"
                            disabled={!backupCodesAcknowledged}
                        >
                            Continue
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (step === 'complete') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <CardTitle className="text-center">Registration Complete!</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Alert>
                            <AlertDescription className="text-center">
                                <div className="text-green-600 font-semibold text-lg mb-2">
                                    <div className="flex justify-center mb-2">
                                        <AnchorbaseLogo width={220} />
                                    </div>
                                </div>
                                <p>
                                    Your account has been successfully set up. Redirecting to
                                    dashboard...
                                </p>
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return null;
}
